import { FC, useEffect } from 'react';
import { observer } from 'mobx-react';
import {
  Box,
  Drawer,
  IconButton,
  Toolbar,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useForm, useStore } from '@tanstack/react-form';
import { AxiosError } from 'axios';
import {
  useGetCompany,
  useUpdateCompanyInfoMutation,
  useCreateCustomerInFortnoxMutation,
} from '../stores/FetcherStores/CompanyFetcher';
import useCompanyId from '../hooks/useCompanyId';
import { useAuth } from '../stores';

interface CompanyEditDrawerProps {
  open: boolean;
  onClose: () => void;
}

const CompanyEditDrawer: FC<CompanyEditDrawerProps> = observer(({ open, onClose }) => {
  const { t } = useTranslation();
  const companyId = useCompanyId();
  const auth = useAuth();
  const { data: company } = useGetCompany(companyId);
  const {
    mutateAsync: updateCompanyInfo,
    isLoading,
    error: updateCompanyInfoError,
  } = useUpdateCompanyInfoMutation();
  const {
    mutateAsync: createCustomerInFortnox,
    isLoading: isCreatingCustomer,
    error: createCustomerInFortnoxError,
  } = useCreateCustomerInFortnoxMutation();

  const form = useForm({
    defaultValues: {
      name: company?.name || '',
      organizationnumber: company?.organizationnumber || '',
      street: company?.street || '',
      city: company?.city || '',
      postalcode: company?.postalcode || '',
      email: company?.email || '',
      phone: company?.phone || '',
      invoicingMode: company?.invoicingMode || 'RESELLER',
      customerId: company?.customerId || '',
    },
    onSubmit: async ({ value }) => {
      await updateCompanyInfo(companyId, {
        ...value,
        invoicingMode: auth.isAdmin ? value.invoicingMode : undefined,
      })
        .then(() => {
          onClose();
        })
        .catch(() => {});
    },
  });

  // Update form values when company data changes
  useEffect(() => {
    if (company && open) {
      form.setFieldValue('name', company.name || '');
      form.setFieldValue('organizationnumber', company.organizationnumber || '');
      form.setFieldValue('street', company.street || '');
      form.setFieldValue('city', company.city || '');
      form.setFieldValue('postalcode', company.postalcode || '');
      form.setFieldValue('email', company.email || '');
      form.setFieldValue('phone', company.phone || '');
      form.setFieldValue('invoicingMode', company.invoicingMode || 'RESELLER');
      form.setFieldValue('customerId', company.customerId || '');
    }
  }, [company, open, form]);

  const invoicingMode = useStore(form.store, (state) => state.values.invoicingMode);

  const handleClose = () => {
    form.reset();
    onClose();
  };
  const error = updateCompanyInfoError || createCustomerInFortnoxError;
  const errorMessage =
    error instanceof AxiosError ? error?.response?.data?.name || error?.message : error?.message;

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Box
        sx={{
          width: '100%',
          maxWidth: 800,
          height: '100%',
          paddingRight: 4,
          paddingLeft: 4,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            marginTop: 12,
            marginBottom: 2,
            display: 'flex',
            minHeight: 'initial !important',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              marginBottom: 2,
            }}
          >
            <Typography variant="h4">Ändra företagsinformation</Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Toolbar>

        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          sx={{ flex: 1, overflowY: 'auto' }}
        >
          {error && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <form.Field name="name">
                {(field) => (
                  <TextField
                    fullWidth
                    label={t('createCompanyScreen.name')}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    margin="normal"
                  />
                )}
              </form.Field>
            </Grid>

            <Grid item xs={12} sm={6}>
              <form.Field name="organizationnumber">
                {(field) => (
                  <TextField
                    fullWidth
                    disabled={company?.hasActivatedCashRegister}
                    label={t('createCompanyScreen.orgNumber')}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    margin="normal"
                  />
                )}
              </form.Field>
            </Grid>

            <Grid item xs={12}>
              <form.Field name="street">
                {(field) => (
                  <TextField
                    fullWidth
                    label={t('createCompanyScreen.address')}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    margin="normal"
                  />
                )}
              </form.Field>
            </Grid>

            <Grid item xs={12} sm={6}>
              <form.Field name="postalcode">
                {(field) => (
                  <TextField
                    fullWidth
                    label={t('createCompanyScreen.zipCode')}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    margin="normal"
                  />
                )}
              </form.Field>
            </Grid>

            <Grid item xs={12} sm={6}>
              <form.Field name="city">
                {(field) => (
                  <TextField
                    fullWidth
                    label={t('createCompanyScreen.municipality')}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    margin="normal"
                  />
                )}
              </form.Field>
            </Grid>

            <Grid item xs={12} sm={6}>
              <form.Field name="email">
                {(field) => (
                  <TextField
                    fullWidth
                    label={t('createCompanyScreen.email')}
                    type="email"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    margin="normal"
                  />
                )}
              </form.Field>
            </Grid>

            <Grid item xs={12} sm={6}>
              <form.Field name="phone">
                {(field) => (
                  <TextField
                    fullWidth
                    label={t('createCompanyScreen.phoneNumber')}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    margin="normal"
                  />
                )}
              </form.Field>
            </Grid>

            {auth.isAdmin && (
              <>
                <Grid item xs={12}>
                  <form.Field name="invoicingMode">
                    {(field) => (
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Invoice Mode</InputLabel>
                        <Select
                          value={field.state.value}
                          label="Invoice Mode"
                          onChange={(e) =>
                            field.handleChange(e.target.value as 'RESELLER' | 'INVOICE' | 'STRIPE')
                          }
                        >
                          <MenuItem value="RESELLER">Reseller</MenuItem>
                          <MenuItem value="INVOICE">Invoice</MenuItem>
                          <MenuItem value="STRIPE">Stripe</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  </form.Field>
                </Grid>
                {invoicingMode === 'INVOICE' && (
                  <Grid item xs={12}>
                    <form.Field name="customerId">
                      {(field) => (
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <TextField
                            fullWidth
                            label="Fortnox customer ID"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            margin="normal"
                          />
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={async () => {
                              await createCustomerInFortnox(companyId)
                                .then(() => {})
                                .catch(() => {});
                            }}
                            disabled={isCreatingCustomer}
                            sx={{ marginTop: 1, minWidth: 'fit-content' }}
                          >
                            {isCreatingCustomer ? 'Skapar...' : 'Skapa i Fortnox'}
                          </Button>
                        </Box>
                      )}
                    </form.Field>
                  </Grid>
                )}
              </>
            )}
          </Grid>

          <Box sx={{ marginTop: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={handleClose} color="error">
              Avbryt
            </Button>
            <Button type="submit" variant="contained" disabled={isLoading}>
              {isLoading ? 'Sparar...' : 'Spara'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
});

export default CompanyEditDrawer;
